import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowUpRight, Instagram, Facebook, Mail } from "lucide-react";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await api.post("/newsletter", { email });
      toast.success("You’re on the list.", {
        description: "We’ll ping you the moment Frostico goes live in Dorset and Hampshire.",
      });
      setEmail("");
    } catch {
      toast.error("Please enter a valid email.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer className="relative border-t border-white/10 bg-[#0A0B10] pt-20 pb-8">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        {/* Massive brand */}
        <div className="border-b border-white/10 pb-16 mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <img
                src="/frostico-logo.png"
                alt="Frostico"
                className="w-full max-w-[520px] h-auto"
              />
              <p className="mt-6 font-display text-4xl md:text-5xl font-bold tracking-tighter leading-[0.95] max-w-md">
                Creamy. Dreamy.{" "}
                <span className="italic text-white/60">Irresistible.</span>
              </p>
            </div>
            <div className="lg:col-span-5">
              <div className="font-mono-accent text-[11px] text-[#5AB0FF] mb-4">
                Get the launch update
              </div>
              <p className="text-white/60 mb-6 leading-relaxed">
                Be first in line for the September 2026 launch across Dorset and Hampshire. No spam, one
                email when we go live.
              </p>
              <form
                onSubmit={subscribe}
                data-testid="newsletter-form"
                className="flex items-center gap-3 border-b border-white/20 focus-within:border-[#8B5CF6] transition-colors"
              >
                <Mail size={16} className="text-white/40" />
                <input
                  data-testid="newsletter-email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="flex-1 bg-transparent outline-none py-3 text-white placeholder:text-white/30"
                />
                <button
                  type="submit"
                  data-testid="newsletter-submit"
                  disabled={loading}
                  className="p-2 text-white/70 hover:text-[#8B5CF6] transition-colors"
                  aria-label="Subscribe"
                >
                  <ArrowUpRight size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div>
            <div className="font-mono-accent text-[10px] text-white/40 mb-4">
              Company
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              <li><a href="#how" className="hover:text-white">How it works</a></li>
              <li><a href="#menu" className="hover:text-white">Menu</a></li>
              <li><a href="#partner" className="hover:text-white">Host a machine</a></li>
              <li><a href="#book" className="hover:text-white">Book for event</a></li>
              <li><a href="#faq" className="hover:text-white">FAQ</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono-accent text-[10px] text-white/40 mb-4">
              Contact
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              <li>
                <a
                  href="mailto:frosticouk@gmail.com"
                  data-testid="email-general"
                  className="hover:text-white transition-colors"
                >
                  frosticouk@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="mailto:frosticouk@gmail.com?subject=Host%20a%20Machine%20Enquiry"
                  data-testid="email-partners"
                  className="hover:text-white transition-colors"
                >
                  Partners · frosticouk@gmail.com
                </a>
              </li>
              <li>
                <a
                  href="mailto:frosticouk@gmail.com?subject=Event%20Booking%20Enquiry"
                  data-testid="email-bookings"
                  className="hover:text-white transition-colors"
                >
                  Bookings · frosticouk@gmail.com
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="font-mono-accent text-[10px] text-white/40 mb-4">
              Follow
            </div>
            <ul className="space-y-3 text-sm text-white/70">
              <li>
                <a
                  href="https://www.instagram.com/frostico.uk/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-instagram"
                  className="inline-flex items-center gap-2 hover:text-[#8B5CF6] transition-colors"
                >
                  <Instagram size={14} /> @frostico.uk
                </a>
              </li>
              <li>
                <a
                  href="https://www.facebook.com/profile.php?id=61591735599780"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-facebook"
                  className="inline-flex items-center gap-2 hover:text-[#8B5CF6] transition-colors"
                >
                  <Facebook size={14} /> Frostico
                </a>
              </li>
              <li>
                <a
                  href="https://www.tiktok.com/@frostico.uk?lang=en"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-tiktok"
                  className="inline-flex items-center gap-2 hover:text-[#8B5CF6] transition-colors"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V9.03a8.16 8.16 0 0 0 4.77 1.52V7.1a4.85 4.85 0 0 1-1.84-.41z" />
                  </svg>
                  @frostico.uk
                </a>
              </li>
            </ul>
          </div>
          <div>
            <div className="font-mono-accent text-[10px] text-white/40 mb-4">
              Launch
            </div>
            <p className="text-sm text-white/70 leading-relaxed">
              Phased rollout across Dorset and Hampshire beginning September 2026. Registered in
              England &amp; Wales.
            </p>
          </div>
        </div>

        <div className="mt-16 pt-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-[11px] font-mono-accent text-white/40">
          <div>© {new Date().getFullYear()} Frostico Ltd. All rights reserved.</div>
          <div>Serving cups of dreamy soft-serve · Since 2026</div>
        </div>
      </div>
    </footer>
  );
}
